import React, { useRef, useEffect } from 'react';
import {
  formatDate,
  prependZero,
  validateName,
} from '../../../../helpers';

import { business, days } from '../Components/constants';

export const invalidnameMsg = 'Invalid Program Name';

export const current = new Date();
export const todaysDate = formatDate(Date.now());
export const minStartTime = `${prependZero(current.getHours())}:${prependZero(
  current.getMinutes(),
)}`;

export const BottomScroll = () => {
  const successRef = useRef(null);
  useEffect(() => successRef.current.scrollIntoView({ behavior: 'smooth' }));
  return <div ref={successRef} />;
};

export const minStartDate = formatDate(
  new Date(current.getFullYear(), current.getMonth(), current.getDate()),
);

export const maxStartDate = formatDate(
  new Date(current.getFullYear(), current.getMonth() + 2, current.getDate()),
);
export const minEndDate = formatDate(
  new Date(current.getFullYear(), current.getMonth(), current.getDate() + 1),
);

const getDateFromString = str => (str ? str.split(' ')[0] : str);

export const handleProgramNameChange = updateFormData => newName => {
  if (newName.length <= 21) {
    updateFormData({
      name: newName,
      nameErrMsg: '',
      showError: false,
    });
  } else {
    updateFormData({
      nameErrMsg: 'Max 20 characters allowed',
    });
  }

  if (!validateName(newName) && newName.length > 0) {
    updateFormData({
      nameErrMsg: invalidnameMsg,
    });
  }
};

export const handleOptionNameChange = updateFormData => newName => {
  if (newName.length <= 21) {
    updateFormData({
      optionName: newName,
      nameErrMsg: '',
      showError: false,
    });
  } else {
    updateFormData({
      nameErrMsg: 'Max 20 characters allowed',
    });
  }

  if (!validateName(newName) && newName.length > 0) {
    updateFormData({
      nameErrMsg: invalidnameMsg,
    });
  }
};

export const handleAmountChange = updateFormData => newAmount => {
  updateFormData({ showError: false });

  const val = parseInt(newAmount, Number);

  if (val >= 0) {
    updateFormData({ amount: val });
    return;
  }

  if (!Number.isInteger(val)) {
    updateFormData({ amount: '' });
  }
};

export const handleStartDateChange = updateFormData => newDate => {
  updateFormData({
    showError: false,
    startDate: newDate,
  });

  if (newDate === formatDate(Date.now())) {
    updateFormData({ startTime: minStartTime });
    return;
  }

  updateFormData({ startTime: '00:00' });
};

export const handleProgramStartTimeChange = updateFormData => newTime => {
  updateFormData({
    showError: false,
    startTime: newTime,
  });
};

export const handleNeverEndCheck = (updateFormData, formData) => () => {
  const updateNeverEnd = !formData.neverEnd;
  const updateEndDate = updateNeverEnd ? '' : formData?.startDate;
  updateFormData({
    showError: false,
    neverEnd: updateNeverEnd,
    endDate: updateEndDate,
  });
};

export const handleEndDateChange = updateFormData => date => {
  updateFormData({
    endDate: date,
    showError: false,
  });
};

export const getProgramBody = programBody => {
  const {
    programId = '',
    name = '',
    selectedGroups = [],
    startDate = '',
    endDate = '',
    neverEnd = false,
    workspaceID = '',
    topupType = 2,
    flowType = 0,
    startTime = '00:00',
    endTime = '23:59',
  } = programBody;

  return {
    name,
    program_id: programId,
    group_ids: selectedGroups,
    start_date: startDate,
    start_time: startTime,
    end_date: endDate,
    end_time: endTime,
    never_ends: neverEnd,
    workspace_id: workspaceID,
    topup_type: topupType,
    flow_type: flowType,
  };
};

const formatTime = time => {
  const [hours, minutes] = time.split(':');
  const date = new Date();
  date.setHours(parseInt(hours, 10), parseInt(minutes, 10), 0, 0);
  return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true });
};

export const transformProgramInfoToPolicyProps = ({
  apiData,
  setEditingPolicy,
  setShowModal,
  deleteOption,
  isViewMode,
}) => {
  if (!apiData?.option_details || apiData.option_details.length === 0) {
    return [];
  }
  return apiData.option_details.map((option, index) => {
    const restrictionDetails = option.restriction_details;
    const applicableServices =
      option.applicable_business_types
        ?.map(
          type =>
            business.find(businessType => businessType.value === type)?.label,
        )
        .filter(Boolean) || [];

    const allDays = new Set();
    restrictionDetails.day_res?.forEach(dayRes => {
      const dayLabel = days.find(day => day.value === dayRes.day)?.label;
      if (dayLabel) allDays.add(dayLabel);
    });
    const applicableDays = Array.from(allDays)
      .filter(Boolean)
      .sort((a, b) => {
        const dayA = days.find(day => day.label === a);
        const dayB = days.find(day => day.label === b);
        return (dayA?.value || 0) - (dayB?.value || 0);
      });

    const slotsMap = new Map();
    restrictionDetails.day_res?.forEach(dayRes => {
      const dayName = days.find(day => day.value === dayRes.day)?.label;
      if (!dayName) return;

      dayRes.slot_res?.forEach(slot => {
        const slotKey = slot.type;

        if (!slotsMap.has(slotKey)) {
          const slotLocations =
            slot.poi_res?.map(location => ({
              poi_id: location.poi_id,
              address: location.address,
              apl_rule: location.apl_rule,
            })) || [];

          slotsMap.set(slotKey, {
            type: slot.type,
            startTime: slot.start_time,
            endTime: slot.end_time,
            amount: slot.amount,
            perOrderLimit: slot.order_res?.per_order_limit_value || 0,
            days: new Set(),
            locations: slotLocations,
            applicabilityRule: slot.poi_res?.[0]?.apl_rule || 0,
          });
        }

        slotsMap.get(slotKey).days.add(dayName);
      });
    });

    const policyQuestions = option.child_question_details?.map(q => q) || [];

    const questionsCount = policyQuestions.length;
    const questionsText =
      questionsCount > 0
        ? `${questionsCount} ${questionsCount === 1 ? 'question' : 'questions'} added`
        : 'NA';

    const transformedSlots = Array.from(slotsMap.values())
      .sort((a, b) => a.type - b.type)
      .map((slot, slotIndex) => {
        const slotDays = Array.from(slot.days)
          .filter(Boolean)
          .sort((a, b) => {
            const dayA = days.find(day => day.label === a);
            const dayB = days.find(day => day.label === b);
            return (dayA?.value || 0) - (dayB?.value || 0);
          });
        const locationCount = slot.locations.length;
        const locationRestriction =
          locationCount > 0 ? `${locationCount} location(s)` : 'NA';
        const slotTitle = `Slot #${slotIndex + 1}: `;
        return {
          title: slotTitle,
          time: `${formatTime(slot.startTime)} to ${formatTime(slot.endTime)}`,
          slotLevelBudget: slot.amount,
          perOrderBudget: slot.perOrderLimit,
          slotLevelDays: slotDays,
          locationRestriction,
          questionsToEmployees: questionsText,
          slotLocations: slot.locations,
          applicabilityRule: slot.applicabilityRule,
          onHoverLocation: () => {},
          onHoverQuestions: () => {},
        };
      });

    return {
      policyTitle: `Policy #${index + 1}: ${option.title}`,
      buttons: isViewMode
        ? []
        : [
            {
              label: 'Edit',
              onClick: () => {
                setEditingPolicy(option);
                setShowModal(true);
              },
            },
            {
              label: 'Delete',
              onClick: deleteOption(option.id),
            },
            {
              label: 'Duplicate',
              onClick: () => {
                const optionCopy = {
                  ...option,
                  id: '',
                  title: `Copy of ${option.title}`,
                };
                setEditingPolicy(optionCopy);
                setShowModal(true);
              },
            },
          ],
      applicableServices,
      applicableDays,
      budgetPerEmployee: restrictionDetails?.amount || 0,
      cycleType: restrictionDetails?.cycle_type || 2,
      slots: transformedSlots,
      policyQuestions,
    };
  });
};

export const transformFormDataToApiFormat = (
  formData,
  companyId,
  questionId,
  optionId,
) => {
  const getDayNumber = dayValue => {
    const dayObj = days.find(day => day.label === dayValue);
    return dayObj ? dayObj.value : dayValue;
  };

  const slotsWithTypes = formData.slots
    .filter(slot => slot.selectedDays && slot.selectedDays.length > 0)
    .sort((a, b) => a.startTime.localeCompare(b.startTime))
    .map((slot, index) => ({
      ...slot,
      slotType: index + 2,
    }));
  const allSelectedDays = formData.selectedDays.map(day =>
    getDayNumber(day.value),
  );

  const dayRes = allSelectedDays.map(dayNumber => {
    const applicableSlots = slotsWithTypes.filter(slot =>
      slot.selectedDays.some(
        slotDay => getDayNumber(slotDay.value) === dayNumber,
      ),
    );

    if (applicableSlots.length === 0) {
      return { day: dayNumber };
    }

    const slotRes = applicableSlots.map(slot => {
      const slotData = {
        start_time: slot.startTime || '00:00',
        end_time: slot.endTime || '23:59',
        amount: parseInt(slot.slotBudget, 10) || 0,
        type: slot.slotType,
      };

      if (
        Array.isArray(slot.selectedLocations) &&
        slot.selectedLocations.length > 0
      ) {
        slotData.poi_res = slot.selectedLocations.map(location => ({
          apl_rule: slot.areLocationsPermitted ? 1 : 2,
          poi_id: parseInt(location.value, 10),
        }));
      }

      if (slot.perOrderBudget && parseInt(slot.perOrderBudget, 10) > 0) {
        slotData.order_res = {
          per_order_limit_value: parseInt(slot.perOrderBudget, 10),
        };
      }

      return slotData;
    });

    return { day: dayNumber, slot_res: slotRes };
  });

  if (optionId && optionId !== '') {
    return {
      company_id: companyId,
      question_id: questionId,
      option_id: optionId,
      option_data: {
        title: formData.optionName,
        restriction_details: {
          name: formData.optionName,
          type: formData?.isAdvancedRulesEnabled ? 1 : 2,
          day_res: dayRes,
          amount: formData.amount,
          cycle_type: formData.cycleType,
        },
        applicable_business_types: formData?.selectedBusinessTypes?.map(
          busType => busType.value,
        ),
        child_question_ids: formData?.selectedQuestions?.map(q => q.value),
      },
    };
  }

  return {
    company_id: companyId,
    question_id: questionId,
    option_data: [
      {
        title: formData.optionName,
        restriction_details: {
          name: formData.optionName,
          type: formData?.isAdvancedRulesEnabled ? 1 : 2,
          day_res: dayRes,
          amount: formData.amount,
          cycle_type: formData.cycleType,
        },
        applicable_business_types: formData?.selectedBusinessTypes?.map(
          busType => busType.value,
        ),
        child_question_ids: formData?.selectedQuestions?.map(q => q.value),
      },
    ],
  };
};

export { getDateFromString };
